import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { QuestionMarkCircleIcon, XMarkIcon } from '@heroicons/react/24/outline';

interface HelpContent {
  title: string;
  sections: {
    title: string;
    content: string;
    tips?: string[];
  }[];
}

const helpContent: Record<string, HelpContent> = {
  '/dashboard': {
    title: 'Dashboard Help',
    sections: [
      {
        title: 'Overview',
        content: 'Your dashboard shows a quick overview of your active hires, upcoming equipment needs, and recent activity.',
        tips: [
          'Click on any hire card to view details',
          'Use the "New Hire" button to quickly create a hire',
          'Check the "Plant Reminders" for upcoming equipment returns'
        ]
      },
      {
        title: 'Quick Actions',
        content: 'The dashboard provides quick access to common tasks like creating new hires, adding sites, and viewing equipment.',
        tips: [
          'Use the floating action buttons for quick access',
          'Recent hires are shown with status indicators',
          'Click on site names to view site details'
        ]
      }
    ]
  },
  '/equipment': {
    title: 'Equipment Help',
    sections: [
      {
        title: 'Equipment Catalog',
        content: 'Browse the community equipment catalog. All equipment is shared across all customers, so you can see what\'s available from the entire community.',
        tips: [
          'Use the search bar to find specific equipment',
          'Filter by category to narrow down results',
          'Click on equipment cards to view details and pricing',
          'Add equipment to favorites for quick access'
        ]
      },
      {
        title: 'Pricing Information',
        content: 'Equipment pricing shows the latest hire rates from the community. This helps you understand market rates and find competitive pricing.',
        tips: [
          'Pricing is updated from recent hires across all customers',
          'Click on pricing to open the hire modal',
          'Pricing includes supplier information and terms'
        ]
      },
      {
        title: 'Creating Hires',
        content: 'Click on any equipment card or pricing to start creating a hire. You\'ll need to select a supplier, site, and dates.',
        tips: [
          'Select your site from the dropdown',
          'Choose your preferred supplier',
          'Set hire start and end dates',
          'Review pricing before confirming'
        ]
      }
    ]
  },
  '/suppliers': {
    title: 'Suppliers Help',
    sections: [
      {
        title: 'Community Suppliers',
        content: 'All suppliers are shared across the community. You can see suppliers created by other customers and add your own.',
        tips: [
          'Blue "Created by you" badge shows suppliers you created',
          'Gray "Community" badge shows suppliers from other customers',
          'Only you can delete suppliers you created',
          'Everyone can edit supplier details and add notes'
        ]
      },
      {
        title: 'Supplier Notes',
        content: 'Add private notes to suppliers for your team. These notes are only visible to your organization.',
        tips: [
          'Click the edit button to add notes',
          'Notes appear in the timeline on the right',
          'Include contact preferences, pricing notes, or special requirements',
          'Notes are private to your organization'
        ]
      },
      {
        title: 'Timeline View',
        content: 'The supplier timeline shows all activity related to that supplier, including hires and notes.',
        tips: [
          'Timeline shows hires created with this supplier',
          'Notes appear with author and timestamp',
          'Events are sorted by date (newest first)',
          'Add notes to track supplier interactions'
        ]
      }
    ]
  },
  '/hires': {
    title: 'Hires Help',
    sections: [
      {
        title: 'Managing Hires',
        content: 'View and manage all your equipment hires. Track status, costs, and supplier information.',
        tips: [
          'Filter hires by status (Active, Completed, etc.)',
          'Click on hire cards to view full details',
          'Edit hires to update dates or add notes',
          'Export to Zahara if you have integration set up'
        ]
      },
      {
        title: 'Hire Status',
        content: 'Hires progress through different statuses: Active, Collection Booked, Completed, etc.',
        tips: [
          'Active hires are currently in progress',
          'Collection Booked means equipment is scheduled for return',
          'Completed hires are finished and archived',
          'Update status as your hire progresses'
        ]
      },
      {
        title: 'Zahara Integration',
        content: 'If you have Zahara integration set up, you can export hires directly to your accounting system.',
        tips: [
          'Look for the "Export to Zahara" button on hires',
          'Integration must be configured in Settings first',
          'Exported hires appear in your Zahara system',
          'Purchase orders can be created automatically'
        ]
      }
    ]
  },
  '/sites': {
    title: 'Sites Help',
    sections: [
      {
        title: 'Site Management',
        content: 'Manage your project sites. Add new sites, update details, and track equipment usage.',
        tips: [
          'Click "Add Site" to create new project locations',
          'Include full address details for accurate mapping',
          'Add job numbers for easy reference',
          'Sites are used when creating hires'
        ]
      },
      {
        title: 'Site Details',
        content: 'Each site includes address information, job numbers, and can be linked to equipment hires.',
        tips: [
          'Complete address helps with supplier coordination',
          'Job numbers help track project-specific equipment',
          'Sites appear in hire creation dropdowns',
          'Edit sites to update information as projects progress'
        ]
      }
    ]
  },
  '/profile': {
    title: 'Profile Help',
    sections: [
      {
        title: 'User Profile',
        content: 'Manage your personal information, profile picture, and account settings.',
        tips: [
          'Update your name and contact information',
          'Upload a profile picture for team recognition',
          'Change your password if needed',
          'Profile information is visible to your team'
        ]
      },
      {
        title: 'Account Settings',
        content: 'Configure your account preferences and integration settings.',
        tips: [
          'Set up Zahara integration for accounting',
          'Configure notification preferences',
          'Manage team access and permissions',
          'Update subscription settings'
        ]
      }
    ]
  }
};

const HelpFAB: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  
  const currentHelp = helpContent[location.pathname] || {
    title: 'General Help',
    sections: [
      {
        title: 'Getting Started',
        content: 'Welcome to Planted! This is a community-driven equipment hire platform.',
        tips: [
          'Browse equipment in the Equipment section',
          'Add suppliers in the Suppliers section',
          'Create hires to book equipment',
          'Manage your sites in the Sites section'
        ]
      }
    ]
  };

  return (
    <>
      {/* Floating Action Button */}
      <div className="fixed bottom-6 right-6 z-50">
        <button
          onClick={() => setIsOpen(true)}
          className="btn btn-circle btn-primary shadow-lg hover:shadow-xl transition-shadow"
          aria-label="Open help"
        >
          <QuestionMarkCircleIcon className="h-6 w-6" />
        </button>
      </div>

      {/* Help Panel - slides out from right */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 z-40"
            onClick={() => setIsOpen(false)}
          />
          
          {/* Slide-out panel */}
          <div className="fixed top-0 right-0 h-full w-96 bg-base-100 shadow-2xl z-50 transform transition-transform duration-300 ease-in-out">
            <div className="h-full flex flex-col">
              {/* Header */}
              <div className="flex justify-between items-center p-6 border-b border-base-300">
                <h3 className="text-lg font-bold">{currentHelp.title}</h3>
                <button
                  onClick={() => setIsOpen(false)}
                  className="btn btn-sm btn-circle btn-ghost"
                  aria-label="Close help"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-6 space-y-6">
                {currentHelp.sections.map((section, index) => (
                  <div key={index} className="border-l-4 border-primary pl-4">
                    <h4 className="font-semibold text-base mb-2">{section.title}</h4>
                    <p className="text-sm text-gray-600 mb-3">{section.content}</p>
                    
                    {section.tips && section.tips.length > 0 && (
                      <div className="bg-base-200 rounded-lg p-3">
                        <h5 className="font-medium text-sm mb-2 text-primary">💡 Tips:</h5>
                        <ul className="text-sm space-y-1">
                          {section.tips.map((tip, tipIndex) => (
                            <li key={tipIndex} className="flex items-start">
                              <span className="text-primary mr-2">•</span>
                              <span>{tip}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Footer */}
              <div className="p-6 border-t border-base-300">
                <button
                  onClick={() => setIsOpen(false)}
                  className="btn btn-primary w-full"
                >
                  Got it!
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default HelpFAB;
